import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../App';
import { Calendar, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LivePage() {
  const navigate = useNavigate();
  const [streamSettings, setStreamSettings] = useState(null);
  const [schedule, setSchedule] = useState([]);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [streamRes, scheduleRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/livestream/settings`),
          fetch(`${BACKEND_URL}/api/schedule`)
        ]);
        
        if (streamRes.ok) setStreamSettings(await streamRes.json());
        if (scheduleRes.ok) setSchedule(await scheduleRes.json());
      } catch (error) {
        console.error('Error fetching stream data:', error);
      }
    };
    
    fetchData();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const isLive = streamSettings?.is_live;
  const embedUrl = streamSettings?.vdo_embed_url;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#071A14] via-[#0A1F17] to-[#0B0F14]">
      <Navigation />
      
      {/* Poker Room Atmosphere */}
      <div className="relative">
        {/* Felt texture overlay */}
        <div className="absolute inset-0 felt-texture opacity-20 pointer-events-none"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8 group"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back</span>
          </button>

          {/* Stream Title with Poker Room Feel */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C9A227] to-[#E1C25B] flex items-center justify-center shadow-lg">
                <span className="text-2xl">🎲</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold text-foreground" data-testid="stream-title">
                {streamSettings?.stream_title || 'The Poker Room'}
              </h1>
              {isLive && (
                <span 
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/20 border-2 border-red-500 rounded-full text-red-400 text-sm font-bold shadow-lg shadow-red-500/20"
                  data-testid="live-badge"
                >
                  <span className="w-3 h-3 bg-red-500 rounded-full live-pulse"></span>
                  LIVE NOW
                </span>
              )}
            </div>
            <p className="text-foreground/70 text-lg max-w-2xl mx-auto">{streamSettings?.stream_description || 'Join the Texas Hold\'em discussion with our panel of experienced players'}</p>
          </div>

          {/* Video Player with Poker Room Border */}
          <div className="mb-12">
            {isLive && embedUrl ? (
              <div className="relative">
                {/* Decorative poker chips corners */}
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#C9A227] rounded-full border-4 border-white/20 shadow-xl z-10 flex items-center justify-center text-xl">
                  ♠
                </div>
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-[#C9A227] rounded-full border-4 border-white/20 shadow-xl z-10 flex items-center justify-center text-xl">
                  ♥
                </div>
                
                <div 
                  className="relative w-full overflow-hidden rounded-2xl border-4 border-[#C9A227]/30 shadow-2xl bg-black"
                  style={{ paddingTop: '56.25%' }}
                  data-testid="live-player-embed"
                >
                  <iframe
                    src={embedUrl}
                    className="absolute top-0 left-0 w-full h-full"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    title="Live Stream"
                  />
                </div>
                
                <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-[#C9A227] rounded-full border-4 border-white/20 shadow-xl z-10 flex items-center justify-center text-xl">
                  ♦
                </div>
                <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-[#C9A227] rounded-full border-4 border-white/20 shadow-xl z-10 flex items-center justify-center text-xl">
                  ♣
                </div>
              </div>
            ) : (
              <div 
                className="relative w-full bg-gradient-to-br from-[#0F3227] to-[#0B0F14] border-4 border-[#C9A227]/20 rounded-2xl shadow-2xl overflow-hidden"
                style={{ paddingTop: '56.25%' }}
                data-testid="offline-placeholder"
              >
                {/* Poker table texture */}
                <div className="absolute inset-0 felt-texture opacity-40"></div>
                
                {/* Card suits pattern */}
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Ctext x='15' y='30' font-size='20' fill='%23C9A227' opacity='0.05'%3E♠%3C/text%3E%3Ctext x='35' y='50' font-size='20' fill='%23C9A227' opacity='0.05'%3E♣%3C/text%3E%3C/svg%3E")`,
                  backgroundSize: '60px 60px'
                }}></div>
                
                <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center p-8 text-center">
                  {streamSettings?.offline_thumbnail_url && (
                    <img 
                      src={streamSettings.offline_thumbnail_url} 
                      alt="Offline" 
                      className="absolute inset-0 w-full h-full object-cover opacity-10"
                    />
                  )}
                  
                  <div className="relative z-10">
                    {/* Poker chips stacked icon */}
                    <div className="relative mx-auto mb-6 w-24 h-24">
                      <div className="absolute inset-0 bg-[#C9A227] rounded-full opacity-20 blur-xl"></div>
                      <div className="relative w-24 h-24 bg-gradient-to-br from-[#C9A227] to-[#E1C25B] rounded-full border-4 border-white/30 shadow-2xl flex items-center justify-center">
                        <span className="text-5xl">🎰</span>
                      </div>
                    </div>
                    
                    <h2 className="text-3xl font-heading font-bold mb-3 text-foreground">Table is Empty</h2>
                    <p className="text-foreground/70 text-lg mb-4">The pros aren't dealing right now</p>
                    <p className="text-foreground/50 text-sm">Check back soon for our next live poker discussion</p>
                    
                    {/* Next stream teaser */}
                    {schedule.length > 0 && (
                      <div className="mt-8 inline-block bg-[#121923] border border-[#C9A227]/30 rounded-xl px-6 py-4">
                        <div className="text-xs text-[#C9A227] font-semibold uppercase tracking-wider mb-1">Next Session</div>
                        <div className="text-foreground font-bold">{schedule[0].title}</div>
                        <div className="text-sm text-foreground/60 mt-1">
                          {new Date(schedule[0].scheduled_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Upcoming Schedule - Poker Room Style */}
          {schedule.length > 0 && (
            <div className="bg-gradient-to-br from-[#121923] to-[#0B0F14] border-2 border-[#C9A227]/20 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
              {/* Decorative pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A227]/5 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[#C9A227]/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-[#C9A227]" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold text-foreground">Upcoming at the Table</h2>
                </div>
                
                <div className="grid gap-4">
                  {schedule.slice(0, 4).map(item => (
                    <div 
                      key={item.schedule_id} 
                      className="flex items-center justify-between p-5 bg-[#0A1F17]/50 border border-[#C9A227]/10 rounded-xl hover:border-[#C9A227]/30 transition-colors"
                    >
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-foreground mb-1 text-lg">{item.title}</h3>
                        <p className="text-sm text-foreground/60">{item.description}</p>
                      </div>
                      <div className="text-right ml-6 min-w-[140px]">
                        <div className="font-bold text-[#C9A227] text-lg">
                          {new Date(item.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="text-sm text-foreground/70 font-medium">
                          {new Date(item.scheduled_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </div>
                        <div className="text-xs text-foreground/50 mt-1">
                          {item.duration_minutes} minutes
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}